import { Injectable } from '@angular/core';
import type { ClientFindInterface } from '@zaoblako/shared/modules/client/dtos/client/client-find.dto';
import type { HelpdeskChangeStatusFormInterface } from '@zaoblako/shared/modules/helpdesk/dto/helpdesk-change-status-form.dto';
import type { HelpdeskNotificationSettingsFindInterface } from '@zaoblako/shared/modules/helpdesk/dto/helpdesk-notification-settings.dto';
import type { HelpdeskPartialUpdateFormInterface } from '@zaoblako/shared/modules/helpdesk/dto/helpdesk-partial-update-form.dto';
import type { HelpdeskTaskFindInterface } from '@zaoblako/shared/modules/helpdesk/dto/helpdesk-task-find.dto';
import type { HelpdeskTaskTypeFindInterface } from '@zaoblako/shared/modules/helpdesk/dto/task-type/helpdesk-task-type-find.dto';
import type { HelpdeskTaskTypeListQueryInterface } from '@zaoblako/shared/modules/helpdesk/dto/task-type/helpdesk-task-type-list-query.dto';
import { HelpdeskTaskStatusEnum } from '@zaoblako/shared/modules/helpdesk/enum/helpdesk.task.status.enum';
import type { HelpdeskRequiredFieldsFindInterface } from '@zaoblako/shared/modules/saas/dtos/helpdesk-required-fields/helpdesk.required.fields.find.dto';
import type { UserGroupFindInterface } from '@zaoblako/shared/modules/security/dtos/user-group/user-group-find.dto';
import type { UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import { HelpDeskSettingsService } from '@zaoblako/web/core/services/helpdesk/help-desk-settings.service';
import { HelpDeskTaskTypeService } from '@zaoblako/web/core/services/helpdesk/help-desk-task-type.service';
import { HelpDeskTaskService } from '@zaoblako/web/core/services/helpdesk/help-desk-task.service';
import { AuthService } from '@zaoblako/web/core/services/security/auth.service';
import { ExternalUserService } from '@zaoblako/web/core/services/security/external-user.service';
import { UserGroupService } from '@zaoblako/web/core/services/security/user-group.service';
import { StoreService } from '@zaoblako/web/core/services/store.service';
import { TitleService } from '@zaoblako/web/core/services/title.service';
import { KeysOfType } from '@zaoblako/web/core/types/keys-of.type';
import { HdTaskState } from '@zaoblako/web/modules/help-desk/components/task/card/models/hd-task-state';
import { hdTaskInitialState } from '@zaoblako/web/modules/help-desk/components/task/card/state/hd-task-initial-state';
import { ClientHelpdeskTaskFormType } from '@zaoblako/web/modules/help-desk/dumbs/helpdesk-accept-task-form/helpdesk-accept-task-form.component';
import { AnalyticalFeaturesHelpdeskTaskFormType } from '@zaoblako/web/modules/help-desk/dumbs/helpdesk-confirm-task-modal/helpdesk-confirm-task-modal.component';
import { BehaviorSubject, forkJoin, of, switchMap } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class HdTaskStoreService extends StoreService<HdTaskState> {
  protected state = new BehaviorSubject(hdTaskInitialState);

  public get getState() {
    return this.state.value;
  }

  public constructor(
    private readonly helpDeskTaskService: HelpDeskTaskService,
    private readonly externalUserService: ExternalUserService,
    private readonly authService: AuthService,
    private readonly userGroupService: UserGroupService,
    private readonly helpDeskTaskTypeService: HelpDeskTaskTypeService,
    private readonly titleService: TitleService,
    private readonly helpDeskSettingsService: HelpDeskSettingsService
  ) {
    super();
  }

  public initState(taskId: number) {
    this.loadTask(taskId);
    this.loadEntities();
    this.loadPanelsInfo();
  }

  public loadTask(taskId: number) {
    this.updateState({ loading: true });

    forkJoin([
      this.helpDeskTaskService.findById<HelpdeskTaskFindInterface>(taskId),
      this.helpDeskTaskService.getRequiredFields()
    ]).subscribe({
      next: ([task, requiredFields]) => {
        this.titleService.setTitle(`Заявка №${task?.id}`);
        this.updateState({ task, requiredFields, loading: false });
        this.loadTaskTypes();
      }
    });
  }

  public loadTaskTypes() {
    const queryParams: HelpdeskTaskTypeListQueryInterface = {};
    if (this.getState.task?.client) {
      queryParams.clientIds = [this.getState.task?.client?.id];
    }
    this.helpDeskTaskTypeService.find<HelpdeskTaskTypeFindInterface>(queryParams).subscribe({
      next: (taskTypes) => {
        const task: HelpdeskTaskFindInterface = { ...this.getState.task };
        task.taskType = taskTypes.find((type) => type?.id === task.taskType?.id);

        this.updateState({
          task,
          taskTypes
        });
      }
    });
  }

  public loadEntities() {
    forkJoin([
      this.userGroupService.find<UserGroupFindInterface>(),
      this.helpDeskSettingsService.getNotificationSettings()
    ]).subscribe(([userGroups, helpdeskNotificationSettings]) => {
      this.updateState({
        userGroups,
        helpdeskNotificationSettings
      });
    });
  }

  public updateFieldTask(key: keyof HelpdeskPartialUpdateFormInterface, value: any) {
    this.helpDeskTaskService.patch({ id: this.getState.task?.id, [key]: value || null }).subscribe();
    const task: HelpdeskTaskFindInterface = { ...this.getState.task, [key]: value || null };
    this.updateState({ task });
  }

  public changeClient(client: ClientFindInterface) {
    this.updateState({ showActionButtons: false });
    const task: HelpdeskTaskFindInterface = this.getState.task;

    this.helpDeskTaskService
      .patch(<HelpdeskPartialUpdateFormInterface & { id?: number }>{
        id: this.getState.task?.id,
        client: client,
        direction: null,
        externalUserAuditors: null
      })
      .pipe(
        switchMap(() =>
          this.getState.task.externalUser
            ? this.externalUserService.patch({
                id: this.getState.task.externalUser?.id,
                client
              })
            : of([])
        )
      )
      .subscribe({
        next: () => {
          this.updateState({
            task: {
              ...task,
              client: client,
              taskType: null,
              externalUserAuditors: []
            },
            showActionButtons: true
          });
          this.loadTaskTypes();
        },
        error: () => {
          this.updateState({ showActionButtons: true });
        }
      });
  }

  public loadFiles() {
    this.updateState({
      filesInfo: {
        files: this.getState.filesInfo.files,
        loading: true
      }
    });

    this.helpDeskTaskService.getFiles(this.getState.task?.id).subscribe({
      next: (files) => {
        this.updateState({
          filesInfo: {
            files,
            loading: false
          }
        });
      }
    });
  }

  public updatePanelsInfo(key: KeysOfType<typeof this.getState.panelsInfo, any>, value: any) {
    const info = this.getState.panelsInfo;
    info[key] = value;
    this.updateState({ panelsInfo: info });
    localStorage.setItem('hdTask.panelsInfo', JSON.stringify(info));
  }

  public loadPanelsInfo() {
    if (localStorage.getItem('hdTask.panelsInfo')) {
      const panelsInfo = JSON.parse(localStorage.getItem('hdTask.panelsInfo'));
      Object.keys(this.getState.panelsInfo).forEach((key) => {
        panelsInfo[key] = panelsInfo.hasOwnProperty(key) ? panelsInfo[key] : this.getState.panelsInfo[key];
      });
      this.updateState({ panelsInfo });
    }
  }

  public changeStatus(data: {
    status: HelpdeskChangeStatusFormInterface;
    clientInfo?: ClientHelpdeskTaskFormType;
    confirmInfo?: AnalyticalFeaturesHelpdeskTaskFormType;
  }): void {
    this.updateState({ showActionButtons: false });

    const changeStatusRequest = (task: HelpdeskTaskFindInterface) => {
      this.helpDeskTaskService.changeStatus(this.getState.task?.id, data.status).subscribe({
        next: (status) => {
          task.status = status;
          if (data.status.status === HelpdeskTaskStatusEnum.IN_PROCESS) {
            task.responsible = this.authService.currentUser;
          }
          this.updateState({ task, showActionButtons: true });
          this.loadTaskTypes();
        },
        error: () => {
          this.updateState({ showActionButtons: true });
        }
      });
    };

    const task: HelpdeskTaskFindInterface = { ...this.getState.task };

    if (data.clientInfo) {
      task.client = data.clientInfo.client;
      task.externalUser = data.clientInfo.externalUser;
      task.taskType = data.clientInfo.taskType;
      task.unit = data.clientInfo.unit;
      if (!task.responsible) {
        task.responsible = this.authService.currentUser;
      }
      this.helpDeskTaskService.update(task).subscribe({
        next: () => {
          changeStatusRequest(task);
        },
        error: () => {
          this.updateState({ showActionButtons: true });
        }
      });
    } else if (data.confirmInfo) {
      task.taskType = data.confirmInfo.taskType;
      task.unit = data.confirmInfo.unit;
      task.deal = data.confirmInfo.deal;
      task.client = data.confirmInfo.client;
      if (
        task.externalUser &&
        task.client?.externalUsers?.length &&
        !task.client.externalUsers.find((externalUser) => externalUser.id === task.externalUser.id)
      ) {
        task.externalUser = null;
      }
      this.helpDeskTaskService.update(task).subscribe({
        next: () => {
          changeStatusRequest(task);
        },
        error: () => {
          this.updateState({ showActionButtons: true });
        }
      });
    } else {
      changeStatusRequest(task);
    }
  }

  public delegate(selectedUser: UserFindInterface) {
    this.updateState({ showActionButtons: false });

    this.helpDeskTaskService.delegate(this.getState.task?.id, { user: selectedUser }).subscribe({
      next: () => {
        const task: HelpdeskTaskFindInterface = { ...this.getState.task };
        task.responsible = selectedUser;
        this.updateState({ task, showActionButtons: true });
      },
      error: () => {
        this.updateState({ showActionButtons: true });
      }
    });
  }

  public toggleEdited(editedField: typeof this.getState.editedField) {
    if (editedField === this.getState.editedField) {
      this.updateState({ editedField: null });
    } else {
      this.updateState({ editedField });
    }
  }

  public changeTask(task: HelpdeskTaskFindInterface) {
    this.updateState({ task: { ...task } });
  }

  public changeCountComment(countComments: number) {
    this.updateState({ countComments });
  }

  public getFactTime() {
    this.helpDeskTaskService.getFactTime(this.getState.task?.id).subscribe({
      next: (data) => {
        const task: HelpdeskTaskFindInterface = { ...this.getState.task };
        task.factMin = data?.factMin;
        this.updateState({ task });
      }
    });
  }

  public updateRequiredFields(requiredFields: HelpdeskRequiredFieldsFindInterface) {
    this.updateState({
      requiredFields: { ...requiredFields }
    });
  }

  public updateDefaultClient(client: ClientFindInterface) {
    this.updateState({
      defaultClient: { ...client }
    });
  }

  public updateNotificationSettings(helpdeskNotificationSettings: HelpdeskNotificationSettingsFindInterface) {
    this.updateState({
      helpdeskNotificationSettings
    });
  }

  public updateFilesCount(count: number) {
    this.updateState((state) => ({ task: { ...state.task, filesCount: state.task.filesCount + count } }));
  }

  public setLoading(loading: boolean) {
    this.updateState({ loading });
  }
}
